/**
 * Custom hook: caly stan i logika biznesowa Kolektora Inwentaryzacyjnego.
 * Wyodrebniony z KolektorPage.jsx aby komponenty-dzieci mogly uzywac
 * wspolnego stanu bez prop-drilling.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from '../../contexts/LocationContext';
import api from '../../services/api';
import collectorService from '../../services/collectorService';
import { warehousePricingService } from '../../services/warehousePricingService';
import { warehousesService } from '../../services/warehousesService';
import cenowkiBufferService from '../../services/cenowkiBufferService';

export default function useCollectorState() {
  const { locationId, selectedLocation } = useLocation();

  // Stan glowny
  const [activeTab, setActiveTab] = useState('sessions'); // 'sessions', 'scan', 'items', 'prices', 'buffer'
  const [currentSession, setCurrentSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});

  // Stan trybu cen specjalnych
  const [priceScannedProduct, setPriceScannedProduct] = useState(null);
  const [priceEditValue, setPriceEditValue] = useState('');
  const [priceBuffer, setPriceBuffer] = useState([]);
  const [bufferLoading, setBufferLoading] = useState(false);
  const [isPriceScannerActive, setIsPriceScannerActive] = useState(false);
  const [warehouseId, setWarehouseId] = useState(null);
  const [priceScanMode, setPriceScanMode] = useState('camera');
  const [priceSearchQuery, setPriceSearchQuery] = useState('');
  const [priceSearchResults, setPriceSearchResults] = useState([]);
  const [priceSearchLoading, setPriceSearchLoading] = useState(false);

  // Stan skanera
  const [isScannerActive, setIsScannerActive] = useState(false);
  const [manualEan, setManualEan] = useState('');
  const [scanMode, setScanMode] = useState('camera');

  // Stan wyszukiwania produktow
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Stan dialogu potwierdzenia ilosci
  const [pendingProduct, setPendingProduct] = useState(null);
  const [pendingQuantity, setPendingQuantity] = useState('');
  const [showQuantityDialog, setShowQuantityDialog] = useState(false);

  // Stan online
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineQueueCount, setOfflineQueueCount] = useState(0);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionType, setNewSessionType] = useState('partial');
  const [showNewSessionForm, setShowNewSessionForm] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  // ============================================
  // FUNKCJE LADOWANIA
  // ============================================

  const loadWarehouseId = async () => {
    try {
      const response = await warehousesService.getWarehousesByLocation(locationId);
      if (response.success && response.data?.length > 0) {
        setWarehouseId(response.data[0].id);
      } else if (Array.isArray(response) && response.length > 0) {
        setWarehouseId(response[0].id);
      } else if (response.data && !Array.isArray(response.data) && response.data.id) {
        setWarehouseId(response.data.id);
      } else {
        const defaultMapping = { 5: 16, 6: 17, 7: 18 };
        const fallbackId = defaultMapping[locationId] || 16;
        setWarehouseId(fallbackId);
      }
    } catch (err) {
      console.error('Blad ladowania warehouse:', err);
      const defaultMapping = { 5: 16, 6: 17, 7: 18 };
      const fallbackId = defaultMapping[locationId] || 16;
      setWarehouseId(fallbackId);
    }
  };

  const loadSessions = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await collectorService.getSessions({
        location_id: locationId,
      });
      if (result.success) {
        setSessions(result.data || []);
      } else {
        setError(result.error || 'Blad ladowania sesji');
      }
    } catch (err) {
      setError('Blad polaczenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const loadSession = async (sessionId, { silent = false } = {}) => {
    // silent=true: NIE ustawiaj globalnego `loading` — sekcja skanera w
    // KolektorPage jest renderowana tylko przy !loading, wiec ustawienie
    // loading odmontowuje i montuje skaner (restart kamery + reset zoomu).
    // Odswiezenie sesji po dodaniu produktu robimy wiec cicho.
    if (!silent) setLoading(true);
    setError('');
    try {
      const result = await collectorService.getSession(sessionId);
      if (result.success) {
        setCurrentSession(result.data.session);
        setItems(result.data.items || []);
        setStats(result.data.stats || {});
      } else {
        setError(result.error || 'Blad ladowania sesji');
      }
    } catch (err) {
      setError('Blad polaczenia z serwerem');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadBuffer = useCallback(async () => {
    if (!locationId) return;
    // Klucz cache scopowany po lokalizacji - bufor jednej lokalizacji nie
    // moze nadpisywac cache innej.
    const storageKey = `cenowki_label_buffer_${locationId}`;
    // Migracja jednorazowa: stary klucz bez sufiksu przenies do klucza
    // biezacej lokalizacji (o ile nowy jeszcze nie istnieje) i usun stary.
    try {
      const legacy = localStorage.getItem('cenowki_label_buffer');
      if (legacy != null) {
        if (localStorage.getItem(storageKey) == null) {
          localStorage.setItem(storageKey, legacy);
        }
        localStorage.removeItem('cenowki_label_buffer');
      }
    } catch {}
    setBufferLoading(true);
    try {
      const result = await cenowkiBufferService.getBuffer(locationId);
      if (result.success && result.data) {
        const items = cenowkiBufferService.parseBufferItems(result.data);
        setPriceBuffer(items);
        localStorage.setItem(storageKey, JSON.stringify(items));
      }
    } catch (err) {
      console.error('Blad ladowania bufora:', err);
      try {
        const cached = JSON.parse(localStorage.getItem(storageKey) || '[]');
        setPriceBuffer(cached);
      } catch {}
    } finally {
      setBufferLoading(false);
    }
  }, [locationId]);

  // Guard blokujacy rownolegle wejscie w synchronizacje (zapobiega podwojnemu zliczeniu)
  const isSyncingRef = useRef(false);
  // Blokada ponownego skanu gdy okno ilosci jest otwarte — zastepuje wczesniejsze
  // zatrzymywanie/restartowanie kamery po kazdym skanie (to resetowalo zoom i
  // powodowalo czarny ekran). Kamera dziala teraz caly czas.
  const scanBusyRef = useRef(false);

  const syncOfflineData = async () => {
    if (!currentSession) return;
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;

    setLoading(true);
    try {
      const result = await collectorService.syncOfflineQueue(currentSession.id);
      if (result.synced > 0) {
        setSuccess(`Zsynchronizowano ${result.synced} pozycji`);
        await loadSession(currentSession.id);
      }
      setOfflineQueueCount(result.remaining ?? 0);
    } catch (err) {
      setError('Blad synchronizacji');
    } finally {
      setLoading(false);
      isSyncingRef.current = false;
    }
  };

  // ============================================
  // EFEKTY
  // ============================================

  // Sledzenie statusu online
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (currentSession) {
        syncOfflineData();
      }
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [currentSession]);

  // Zaladuj sesje przy starcie i przy zmianie lokalizacji
  useEffect(() => {
    setCurrentSession(null);
    setItems([]);
    setActiveTab('sessions');
    setIsScannerActive(false);
    setPriceScannedProduct(null);
    setIsPriceScannerActive(false);

    loadSessions();
    loadWarehouseId();
    setOfflineQueueCount(collectorService.getOfflineQueue().length);
  }, [locationId]);

  // Laduj bufor cenowek z serwera przy zmianie lokalizacji
  useEffect(() => {
    loadBuffer();
  }, [loadBuffer]);

  // Aktywuj skaner cen gdy jestesmy na zakladce 'prices'
  useEffect(() => {
    if (activeTab === 'prices' && !priceScannedProduct) {
      const timer = setTimeout(() => {
        setIsPriceScannerActive(true);
      }, 100);
      return () => clearTimeout(timer);
    } else if (activeTab !== 'prices') {
      setIsPriceScannerActive(false);
    }
  }, [activeTab, priceScannedProduct]);

  // Automatycznie aktywuj skaner gdy jestesmy na zakladce 'scan' z aktywna sesja
  useEffect(() => {
    if (activeTab === 'scan' && currentSession && scanMode === 'camera' && !showQuantityDialog) {
      const timer = setTimeout(() => {
        if (!showQuantityDialog) {
          setIsScannerActive(true);
        }
      }, 100);
      return () => clearTimeout(timer);
    } else if (activeTab !== 'scan') {
      setIsScannerActive(false);
    }
  }, [activeTab, currentSession, scanMode, showQuantityDialog]);

  // ============================================
  // SESJE
  // ============================================

  const handleCreateSession = async () => {
    if (!newSessionName.trim()) {
      setError('Podaj nazwe sesji');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const sessionData = {
        location_id: locationId,
        name: newSessionName.trim(),
        inventory_type: newSessionType
      };
      if (newSessionType === 'delivery_check' && selectedInvoiceId) {
        sessionData.invoice_id = selectedInvoiceId;
      }
      const result = await collectorService.createSession(sessionData);

      if (result.success) {
        const typeLabel = newSessionType === 'full' ? 'calosciowa' : 'czastkowa';
        setSuccess(`Sesja utworzona (${typeLabel})`);
        setShowNewSessionForm(false);
        setNewSessionName('');
        setNewSessionType('partial');
        await loadSessions();
        await loadSession(result.data.id);
        setScanMode('camera');
        setIsScannerActive(true);
        setActiveTab('scan');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Blad tworzenia sesji');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSession = async (session) => {
    await loadSession(session.id);
    setScanMode('camera');
    setIsScannerActive(true);
    setActiveTab('scan');
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Czy na pewno usunac te sesje?')) return;

    setLoading(true);
    try {
      const result = await collectorService.deleteSession(sessionId);
      if (result.success) {
        setSuccess('Sesja usunieta');
        if (currentSession?.id === sessionId) {
          setCurrentSession(null);
          setItems([]);
          setActiveTab('sessions');
        }
        await loadSessions();
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Blad usuwania sesji');
    } finally {
      setLoading(false);
    }
  };

  const handlePauseSession = async () => {
    if (!currentSession) return;
    setLoading(true);
    try {
      const result = await collectorService.pauseSession(currentSession.id);
      if (result.success) {
        setCurrentSession({ ...currentSession, status: 'paused' });
        setSuccess('Sesja wstrzymana');
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (err) {
      setError('Blad wstrzymywania sesji');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToInventory = async () => {
    if (!currentSession) return;
    if (!window.confirm('Czy na pewno chcesz utworzyc inwenture z tej sesji? Tej operacji nie mozna cofnac.')) return;

    setLoading(true);
    try {
      const result = await collectorService.convertToInventory(currentSession.id);
      if (result.success) {
        setSuccess(`Utworzono inwenture: ${result.data.document_number}`);
        setCurrentSession(null);
        setItems([]);
        setActiveTab('sessions');
        await loadSessions();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Blad tworzenia inwentury');
    } finally {
      setLoading(false);
    }
  };

  // Raport różnic dla kontroli dostawy
  const [deliveryReport, setDeliveryReport] = useState(null);
  const [showDeliveryReport, setShowDeliveryReport] = useState(false);

  const handleShowDeliveryReport = async () => {
    if (!currentSession) return;
    setLoading(true);
    try {
      // Najpierw zapisz raport
      const saveResult = await api.post(`/collector/sessions/${currentSession.id}/save-delivery-report`);
      // Potem pobierz podgląd
      const result = await api.get(`/collector/sessions/${currentSession.id}/delivery-report`);
      if (result.data?.success) {
        setDeliveryReport(result.data.data);
        setShowDeliveryReport(true);
        if (saveResult.data?.success) {
          setSuccess('Raport zapisany');
          setTimeout(() => setSuccess(''), 2000);
        }
      } else {
        setError(result.data?.error || 'Błąd pobierania raportu');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Błąd pobierania raportu');
    } finally {
      setLoading(false);
    }
  };

  // Utwórz PW z sesji kolektora
  const handleCreatePw = async () => {
    if (!currentSession) return;
    if (!window.confirm('Czy na pewno chcesz utworzyc PW z tej sesji?')) return;

    setLoading(true);
    try {
      const result = await api.post(`/collector/sessions/${currentSession.id}/create-pw`);
      if (result.data?.success) {
        setSuccess(`PW utworzone — ${result.data.data?.items_count || 0} pozycji`);
        setCurrentSession(null);
        setItems([]);
        setActiveTab('sessions');
        await loadSessions();
      } else {
        setError(result.data?.error || 'Błąd tworzenia PW');
      }
    } catch (err) {
      console.error('Błąd tworzenia PW:', err);
      const errMsg = err?.response?.data?.error || err?.message || 'Błąd tworzenia PW';
      setError(errMsg);
      // Przeladuj sesje na wszelki wypadek (PW moglo sie utworzyc mimo bledu statusu)
      await loadSessions();
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // SKANOWANIE
  // ============================================

  const handleScan = useCallback(async (ean) => {
    if (!currentSession) {
      setError('Najpierw wybierz sesje');
      return;
    }
    // Ignoruj skany gdy okno ilosci juz otwarte (kamera dziala caly czas)
    if (scanBusyRef.current) return;
    scanBusyRef.current = true;

    setError('');
    setSuccess('');

    setPendingProduct({ ean, name: null, loading: true });
    setPendingQuantity('');
    setShowQuantityDialog(true);

    try {
      const result = await collectorService.getProductByEan(ean, locationId);
      if (result && result.success && result.data) {
        setPendingProduct({
          ean,
          name: result.data.name || result.data.nazwa,
          code: result.data.code || result.data.kod,
          unit: result.data.unit || result.data.jednostka || 'szt',
          system_count: result.data.stock || result.data.stan || 0,
          loading: false
        });
      } else {
        setPendingProduct({ ean, name: null, loading: false });
      }
    } catch (err) {
      setPendingProduct({ ean, name: null, loading: false });
    }
  }, [currentSession, locationId]);

  const handleConfirmQuantity = async () => {
    if (!pendingProduct || !currentSession) return;

    const quantity = parseFloat(pendingQuantity) || 1;
    if (quantity <= 0) {
      setError('Ilosc musi byc wieksza od 0');
      return;
    }

    // NIE ustawiamy globalnego `loading` — to odmontowuje skaner (restart
    // kamery + reset zoomu po kazdym dodaniu). Dodanie i tak jest szybkie.
    setError('');

    // Identyfikatory pozycji: EAN (jesli dostepny) lub product_id (produkt bez EAN z wyszukiwarki)
    const ean = pendingProduct.ean || '';
    const productId = pendingProduct.product_id ?? null;
    const label = ean || pendingProduct.name || (productId != null ? `#${productId}` : '');

    const queueOffline = () => {
      collectorService.addToOfflineQueue({
        type: 'scan',
        sessionId: currentSession.id,
        ean,
        productId,
        quantity
      });
      setOfflineQueueCount(prev => prev + 1);
      setSuccess(`[OFFLINE] Zapisano: ${label} x ${quantity}`);
      setTimeout(() => setSuccess(''), 2000);
    };

    try {
      if (isOnline) {
        const result = await collectorService.scan(currentSession.id, ean, quantity, productId);

        if (result && result.success) {
          const productName = result.data?.product_name || label;
          setSuccess(`Dodano: ${productName} x ${quantity}`);
          try {
            // Cicho — bez globalnego loading, zeby nie odmontowac skanera
            await loadSession(currentSession.id, { silent: true });
          } catch (loadErr) {
            console.error('Blad ladowania sesji:', loadErr);
          }
          setTimeout(() => setSuccess(''), 2000);
        } else {
          const errorMsg = result?.error || 'Nie znaleziono produktu';
          setError(errorMsg);
          setTimeout(() => setError(''), 4000);
        }
      } else {
        queueOffline();
      }
    } catch (err) {
      // Blad sieci przy zadaniu online - nie gubimy skanu, zapisujemy do kolejki offline
      console.error('Blad dodawania (fallback do offline):', err);
      queueOffline();
    } finally {
      setShowQuantityDialog(false);
      setPendingProduct(null);
      setPendingQuantity('1');
      scanBusyRef.current = false; // gotowy na kolejny skan (kamera nie restartuje)
    }
  };

  const handleCancelScan = () => {
    setShowQuantityDialog(false);
    setPendingProduct(null);
    setPendingQuantity('1');
    setError('');
    scanBusyRef.current = false; // gotowy na kolejny skan (kamera nie restartuje)
  };

  const handleManualScan = () => {
    if (!manualEan.trim()) return;
    handleScan(manualEan.trim());
    setManualEan('');
  };

  // ============================================
  // TRYB CEN SPECJALNYCH
  // ============================================

  const handlePriceScan = useCallback(async (ean) => {
    setIsPriceScannerActive(false);
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const searchResult = await collectorService.searchProducts(ean, locationId, 5);

      let product = null;
      if (searchResult.success && searchResult.data?.products?.length > 0) {
        product = searchResult.data.products.find(p =>
          p.barcode === ean || p.ean === ean
        ) || searchResult.data.products[0];
      }

      if (!product) {
        const productResult = await collectorService.getProductByEan(ean, locationId);
        if (productResult?.success && productResult?.data) {
          product = productResult.data;
        }
      }

      if (!product) {
        setError(`Nie znaleziono produktu: ${ean}`);
        setLoading(false);
        return;
      }

      const stawkaVat = product.stawka_vat || 8;
      // Kaucja (stawka_vat=-2 lub typ_produktu='kaucja'): netto == brutto
      // (NIE dzielic przez (1+VAT)) i bez liczenia marzy (null).
      const isKaucja = Number(stawkaVat) === -2 || product.typ_produktu === 'kaucja';
      // Dzielnik VAT tylko dla VAT > 0 (dla kaucji/zw/0% netto = brutto)
      const vatDivisor = (!isKaucja && Number(stawkaVat) > 0) ? (1 + stawkaVat / 100) : 1;
      // current_purchase_price / cena_zakupu sa cenami BRUTTO - netto wyliczamy dzielac przez VAT.
      // Preferuj jawne pola netto jesli backend je zwraca.
      const cenaZakupuBrutto = product.current_purchase_price || product.cena_zakupu || 0;
      const cenaZakupuNetto = isKaucja
        ? cenaZakupuBrutto
        : (product.cena_zakupu_netto != null
          ? product.cena_zakupu_netto
          : cenaZakupuBrutto / vatDivisor);

      const cenaSprzedazyBrutto = product.special_price_brutto || product.price || product.cena_sprzedazy_brutto || product.cena || 0;
      const cenaSprzedazyNetto = isKaucja
        ? cenaSprzedazyBrutto
        : (product.special_price_netto || product.default_price_netto || (cenaSprzedazyBrutto / vatDivisor));

      const marginAmount = isKaucja
        ? null
        : (product.margin_amount || (cenaSprzedazyNetto - cenaZakupuNetto));
      const marginPercent = isKaucja
        ? null
        : (product.margin_percent || (cenaSprzedazyNetto > 0 ? (marginAmount / cenaSprzedazyNetto * 100) : 0));

      // Pobierz marżę FIFO z API (kaucja wykluczona z marzy - nie pobieramy)
      let fifoData = { fifo_purchase_price: null, fifo_margin_percent: null, fifo_margin_amount: null };
      if (!isKaucja) {
        try {
          const { marginService } = await import('../../services/marginService');
          const marginResult = await marginService.calculateMargin({
            product_id: product.id,
            sell_price_brutto: cenaSprzedazyBrutto,
            warehouse_id: locationId || 5
          });
          if (marginResult.success) {
            fifoData = {
              // ?? zamiast || - realne 0 (marza 0%, kwota 0) to prawdziwa wartosc,
              // nie brak. Wczesniej 0 || null gubilo 0% i display spadal na marze
              // z ostatniej faktury.
              fifo_purchase_price: marginResult.fifo_purchase_price ?? null,
              fifo_margin_percent: marginResult.fifo_margin_percent ?? null,
              fifo_margin_amount: marginResult.fifo_margin_amount ?? null
            };
          }
        } catch (e) {
          console.error('FIFO margin fetch error:', e);
        }
      }

      const scannedProduct = {
        id: product.id,
        ean: product.barcode || product.ean || ean,
        name: product.name || product.nazwa,
        code: product.code || product.kod_produktu,
        stawka_vat: stawkaVat,
        is_kaucja: isKaucja,
        cena_zakupu_netto: cenaZakupuNetto,
        cena_zakupu_brutto: cenaZakupuBrutto,
        cena_sprzedazy_netto: cenaSprzedazyNetto,
        cena_sprzedazy_brutto: cenaSprzedazyBrutto,
        has_special_price: product.has_special_price || false,
        margin_percent: marginPercent,
        margin_amount: marginAmount,
        fifo_purchase_price: fifoData.fifo_purchase_price,
        fifo_margin_percent: fifoData.fifo_margin_percent,
        fifo_margin_amount: fifoData.fifo_margin_amount,
        stock: product.stock_quantity || product.stock || product.stan || 0
      };

      setPriceScannedProduct(scannedProduct);
      setPriceEditValue(scannedProduct.cena_sprzedazy_brutto.toFixed(2));

    } catch (err) {
      setError('Blad pobierania danych produktu');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  const handleUpdatePrice = async () => {
    if (!priceScannedProduct) {
      setError('Brak produktu');
      return;
    }

    if (!warehouseId && !locationId) {
      setError('Magazyn nie zostal zaladowany. Odswiez strone.');
      return;
    }

    const newPriceBrutto = parseFloat(priceEditValue);
    if (isNaN(newPriceBrutto) || newPriceBrutto <= 0) {
      setError('Nieprawidlowa cena');
      return;
    }

    setLoading(true);
    try {
      const stawkaVat = priceScannedProduct.stawka_vat || 8;
      // Kaucja: netto == brutto (bez dzielenia przez VAT), bez marzy
      const isKaucja = priceScannedProduct.is_kaucja || Number(stawkaVat) === -2;
      const vatRate = (!isKaucja && Number(stawkaVat) > 0) ? (1 + stawkaVat / 100) : 1;
      const newPriceNetto = newPriceBrutto / vatRate;

      const effectiveWarehouseId = warehouseId || locationId;

      const result = await warehousePricingService.setWarehousePrice(
        effectiveWarehouseId,
        priceScannedProduct.id,
        {
          cena_sprzedazy_netto: Math.round(newPriceNetto * 100) / 100,
          cena_sprzedazy_brutto: newPriceBrutto,
          created_by: 'kolektor'
        }
      );

      if (result.success) {
        setSuccess(`Cena zaktualizowana: ${newPriceBrutto.toFixed(2)} zl`);
        setPriceScannedProduct(prev => ({
          ...prev,
          cena_sprzedazy_brutto: newPriceBrutto,
          cena_sprzedazy_netto: newPriceNetto,
          has_special_price: true,
          margin_amount: isKaucja ? null : (newPriceNetto - prev.cena_zakupu_netto),
          margin_percent: !isKaucja && newPriceNetto > 0
            ? ((newPriceNetto - prev.cena_zakupu_netto) / newPriceNetto * 100)
            : (isKaucja ? null : 0),
          fifo_margin_amount: !isKaucja && prev.fifo_purchase_price != null ? (newPriceNetto - prev.fifo_purchase_price) : null,
          fifo_margin_percent: !isKaucja && prev.fifo_purchase_price != null && newPriceNetto > 0
            ? ((newPriceNetto - prev.fifo_purchase_price) / newPriceNetto * 100)
            : null
        }));
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Blad aktualizacji ceny');
      }
    } catch (err) {
      console.error('Blad zapisywania ceny:', err);
      const errorMsg = err.message || err.toString();
      if (errorMsg.includes('403') || errorMsg.includes('Forbidden')) {
        setError('Brak uprawnien. Zaloguj sie ponownie.');
      } else if (errorMsg.includes('404')) {
        setError('Nie znaleziono produktu lub magazynu');
      } else {
        setError(`Blad: ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToBuffer = async () => {
    if (!priceScannedProduct) return;

    if (priceBuffer.find(p => p.id === priceScannedProduct.id)) {
      setError('Produkt juz jest w buforze');
      setTimeout(() => setError(''), 2000);
      return;
    }

    const productForBuffer = {
      id: priceScannedProduct.id,
      nazwa: priceScannedProduct.name,
      nazwa_uproszczona: priceScannedProduct.name,
      finalName: priceScannedProduct.name,
      ean: priceScannedProduct.ean,
      kod_produktu: priceScannedProduct.code,
      cena_sprzedazy_brutto: priceScannedProduct.cena_sprzedazy_brutto,
      cena_sprzedazy_netto: priceScannedProduct.cena_sprzedazy_netto,
      stawka_vat: priceScannedProduct.stawka_vat,
      cena_zakupu: priceScannedProduct.cena_zakupu_netto,
      has_special_price: priceScannedProduct.has_special_price,
      gramatura: priceScannedProduct.gramatura,
      jednostka_wagi: priceScannedProduct.jednostka_wagi
    };

    const result = await cenowkiBufferService.addToBuffer(locationId, productForBuffer, 'kolektor');

    if (result.success) {
      await loadBuffer();
      setSuccess('Dodano do bufora cenowek');
      setTimeout(() => setSuccess(''), 2000);
    } else {
      setError(result.error || 'Blad dodawania do bufora');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleRemoveFromBuffer = async (item) => {
    // item.bufferId = id wiersza cenowki_buffer, item.id = product_id.
    // Backend: DELETE /cenowki/buffer/<item_id> kasuje po id wiersza,
    // DELETE /cenowki/buffer/product/<product_id> po id produktu.
    // Nie wolno wysylac bufferId na endpoint produktowy (404 / zly produkt).
    try {
      let result;
      if (item?.bufferId != null) {
        const response = await api.delete(`/cenowki/buffer/${item.bufferId}`, {
          params: { location_id: locationId }
        });
        result = response.data;
      } else {
        // Fallback: brak id wiersza — usun po product_id
        result = await cenowkiBufferService.removeFromBuffer(locationId, item?.id ?? item);
      }
      if (result?.success) {
        await loadBuffer();
      } else {
        setError(result?.error || 'Blad usuwania z bufora');
        setTimeout(() => setError(''), 2000);
      }
    } catch (err) {
      console.error('Blad usuwania z bufora:', err);
      setError('Blad usuwania z bufora');
      setTimeout(() => setError(''), 2000);
    }
  };

  const handleClearBuffer = async () => {
    if (window.confirm('Czy na pewno wyczyscic bufor cenowek?')) {
      const result = await cenowkiBufferService.clearBuffer(locationId);
      if (result.success) {
        setPriceBuffer([]);
        localStorage.setItem(`cenowki_label_buffer_${locationId}`, '[]');
      } else {
        setError(result.error || 'Blad czyszczenia bufora');
      }
    }
  };

  const handlePrintBuffer = () => {
    window.open('/location-pricing?showBuffer=true', '_blank');
  };

  const handleClearPriceProduct = () => {
    setPriceScannedProduct(null);
    setPriceEditValue('');
    setPriceSearchQuery('');
    setPriceSearchResults([]);
    if (priceScanMode === 'camera') {
      setIsPriceScannerActive(true);
    }
  };

  const handlePriceSearch = async (query) => {
    setPriceSearchQuery(query);

    if (!query || query.length < 2) {
      setPriceSearchResults([]);
      return;
    }

    setPriceSearchLoading(true);
    try {
      const result = await collectorService.searchProducts(query, locationId, 15);
      if (result.success && result.data) {
        const products = result.data.products || result.data || [];
        setPriceSearchResults(Array.isArray(products) ? products : []);
      } else {
        setPriceSearchResults([]);
      }
    } catch (err) {
      console.error('Blad wyszukiwania:', err);
      setPriceSearchResults([]);
    } finally {
      setPriceSearchLoading(false);
    }
  };

  const handleSelectPriceProduct = async (product) => {
    setPriceSearchResults([]);
    setPriceSearchQuery('');
    const ean = product.ean || product.kod_produktu || product.barcode;
    if (ean) {
      await handlePriceScan(ean);
    }
  };

  // ============================================
  // WYSZUKIWANIE PRODUKTOW
  // ============================================

  const handleSearch = async (query) => {
    setSearchQuery(query);

    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const result = await collectorService.searchProducts(query, locationId, 15);
      if (result.success && result.data) {
        const products = result.data.products || result.data || [];
        setSearchResults(Array.isArray(products) ? products : []);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Blad wyszukiwania:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectProduct = (product) => {
    setSearchResults([]);
    setSearchQuery('');
    setIsScannerActive(false);

    setPendingProduct({
      ean: product.barcode || product.ean || '',
      product_id: product.id,
      name: product.name,
      code: product.kod_produktu || product.code || '',
      unit: product.unit || 'szt',
      system_count: product.stock_quantity || 0,
      price: product.price
    });
    setPendingQuantity('');
    setShowQuantityDialog(true);
  };

  // ============================================
  // EDYCJA POZYCJI
  // ============================================

  const handleUpdateItem = async (itemId, newCount) => {
    if (!currentSession) return;

    try {
      const result = await collectorService.updateItem(currentSession.id, itemId, {
        scanned_count: parseFloat(newCount)
      });
      if (result.success) {
        setEditingItem(null);
        await loadSession(currentSession.id);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Blad aktualizacji');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!currentSession) return;
    if (!window.confirm('Usunac te pozycje?')) return;

    try {
      const result = await collectorService.deleteItem(currentSession.id, itemId);
      if (result.success) {
        await loadSession(currentSession.id);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Blad usuwania');
    }
  };

  return {
    // Location
    locationId,
    selectedLocation,

    // Main state
    activeTab, setActiveTab,
    currentSession,
    sessions,
    items,
    stats,

    // Price mode
    priceScannedProduct,
    priceEditValue, setPriceEditValue,
    priceBuffer,
    bufferLoading,
    isPriceScannerActive, setIsPriceScannerActive,
    warehouseId,
    priceScanMode, setPriceScanMode,
    priceSearchQuery,
    priceSearchResults,
    priceSearchLoading,

    // Scanner
    isScannerActive, setIsScannerActive,
    manualEan, setManualEan,
    scanMode, setScanMode,

    // Product search
    searchQuery,
    searchResults,
    searchLoading,

    // Quantity dialog
    pendingProduct,
    pendingQuantity, setPendingQuantity,
    showQuantityDialog,

    // Online
    isOnline,
    offlineQueueCount,

    // UI
    loading,
    error, setError,
    success,
    editingItem, setEditingItem,
    newSessionName, setNewSessionName,
    newSessionType, setNewSessionType,
    showNewSessionForm, setShowNewSessionForm,
    selectedInvoiceId, setSelectedInvoiceId,
    locationId,

    // Session handlers
    handleCreateSession,
    handleSelectSession,
    handleDeleteSession,
    handlePauseSession,
    handleConvertToInventory,
    handleCreatePw,
    handleShowDeliveryReport,
    deliveryReport, showDeliveryReport, setShowDeliveryReport,

    // Scan handlers
    handleScan,
    handleConfirmQuantity,
    handleCancelScan,
    handleManualScan,
    syncOfflineData,

    // Price handlers
    handlePriceScan,
    handleUpdatePrice,
    handleAddToBuffer,
    handleRemoveFromBuffer,
    handleClearBuffer,
    handlePrintBuffer,
    handleClearPriceProduct,
    handlePriceSearch,
    handleSelectPriceProduct,

    // Search handlers
    handleSearch,
    handleSelectProduct,

    // Item handlers
    handleUpdateItem,
    handleDeleteItem,
  };
}
